<template>
    <div class="valorant-record-sections">
        <section v-if="hasPerformance" class="record-section performance-section">
            <h2 class="section-title">表现数据</h2>
            <div class="performance-grid">
                <div v-if="accuracyData" class="accuracy-card detail-card">
                    <div class="accuracy-content">
                        <img class="accuracy-figure" :src="accuracyIcon" alt="" />
                        <div class="accuracy-values">
                            <div v-for="row in accuracyRows" :key="row.key" class="accuracy-row" :class="{ accent: row.accent }">
                                <span>{{ row.label }}</span>
                                <em>{{ row.percentage }}</em>
                                <b>{{ row.hits }} <small>次</small></b>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="roles-grid">
                    <div v-for="role in roles" :key="role.id || role.name" class="role-card detail-card" :class="{ missing: role.missing }">
                        <div class="role-summary">
                            <img :src="roleIcon(role.name, role.imageUrl)" alt="" />
                            <div class="role-overview">
                                <div class="role-heading">
                                    <span>{{ localizeRole(role.name) }}</span>
                                    <span>{{ roleMatchesText(role) }}</span>
                                </div>
                                <div class="role-progress"><i :style="{ width: roleWidth(role) }"></i></div>
                            </div>
                        </div>
                        <div class="role-values">
                            <span><b>{{ roleMetric(role, "winRate", true) }}</b><small>胜率</small></span>
                            <span><b>{{ winLossText(role) }}</b><small>胜负</small></span>
                            <span><b>{{ roleMetric(role, "kda") }}</b><small>KDA</small></span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="agents.length" class="record-section agents-section">
            <h2 class="section-title">我的特工</h2>
            <div class="table-head agent-head">
                <span class="agent-avatar-head">特工</span>
                <div class="agent-head-values">
                    <span>场次</span><span>胜率</span><span>K/D</span><span>ACS</span><span>ADR</span>
                </div>
            </div>
            <div class="detail-table agent-table">
                <div v-for="agent in visibleAgents" :key="agent.id || agent.name" class="table-row agent-row">
                    <img class="agent-avatar" :src="agentImage(agent)" alt="" />
                    <div class="agent-row-values">
                        <div class="agent-primary">
                            <strong>{{ localizeAgent(agent.name, agentNameMap) }}</strong>
                            <small>{{ formatInteger(agent.matches) }}场</small>
                        </div>
                        <span>{{ formatPercent(agent.winRate) }}</span>
                        <span>{{ formatDecimal(agent.kd, 1) }}</span>
                        <span>{{ formatDecimal(agent.acs, 1) }}</span>
                        <span>{{ formatDecimal(agent.adr, 1) }}</span>
                    </div>
                </div>
            </div>
            <button v-if="agents.length > initialRows" type="button" class="expand-button" @click="toggleAgents">
                {{ agentsExpanded ? "收起特工" : "全部特工" }}
                <img :class="{ expanded: agentsExpanded }" :src="chevronDown" alt="" />
            </button>
        </section>

        <section v-if="showWeapons && weapons.length" class="record-section weapons-section">
            <h2 class="section-title">我的武器</h2>
            <div class="table-head weapon-head">
                <span>武器</span><span>击杀数</span><span>爆头率</span><span>场均伤害</span><span>K/D</span>
            </div>
            <div class="detail-table weapon-table">
                <div v-for="weapon in visibleWeapons" :key="weapon.id || weapon.name" class="table-row weapon-row">
                    <div class="weapon-image-cell"><img :src="weapon.imageUrl" alt="" /></div>
                    <div class="weapon-kill-cell">
                        <div>
                            <strong>{{ localizeWeapon(weapon.name) }}</strong>
                            <span>{{ formatInteger(weapon.kills) }}</span>
                        </div>
                        <i><b :style="{ width: killWidth(weapon) }"></b></i>
                    </div>
                    <span>{{ formatPercent(weapon.headshotPercentage) }}</span>
                    <span>{{ formatDecimal(weapon.damagePerMatch, 1) }}</span>
                    <span>{{ formatDecimal(weapon.kd, 1) }}</span>
                </div>
            </div>
            <button v-if="weapons.length > initialRows" type="button" class="expand-button" @click="weaponsExpanded = !weaponsExpanded">
                {{ weaponsExpanded ? "收起武器" : "全部武器" }}
                <img :class="{ expanded: weaponsExpanded }" :src="chevronDown" alt="" />
            </button>
        </section>

        <section class="record-section matches-section">
            <div class="section-heading">
                <h2 class="section-title">近期战绩</h2>
                <button v-if="matches.length && matchNavigationEnabled" type="button" class="all-link" @click="$emit('open-list')">
                    全部<img :src="chevronRight" alt="" />
                </button>
            </div>
            <div v-if="matches.length" class="match-list">
                <valorant-match-card
                    v-for="match in visibleMatches"
                    :key="match.trackerId || match.id || `${match.gameTime}-${match.heroNameCn}`"
                    :match="match"
                    :clickable="matchNavigationEnabled"
                    @select="openMatch"
                />
            </div>
            <div v-else class="record-empty"></div>
        </section>
    </div>
</template>

<script>
import ValorantMatchCard from "../ValorantMatchCard/ValorantMatchCard.vue";

const details = require("../../assets/js/valorantRecordDetails");

export default {
    name: "ValorantRecordSections",
    components: {
        ValorantMatchCard,
    },
    props: {
        trackerInfo: {
            type: Object,
            default: () => ({}),
        },
        matchNavigationEnabled: {
            type: Boolean,
            default: true,
        },
        showWeapons: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            agentsExpanded: false,
            weaponsExpanded: false,
            initialRows: 6,
            accuracyIcon: "https://image.gamersky.com/webimg13/valorant_track/accuracy.af61a35e.svg",
            chevronDown: "https://image.gamersky.com/webimg13/valorant_track/chevron-down.53a71dca.svg",
            chevronRight: "https://image.gamersky.com/webimg13/valorant_track/chevron-right.ce96545f.svg",
        };
    },
    computed: {
        detailData() {
            return this.trackerInfo.detailData || {};
        },
        accuracyData() {
            const accuracy = this.detailData.accuracy;
            return accuracy && accuracy.data ? accuracy.data : null;
        },
        accuracyRows() {
            const source = this.accuracyData || {};
            const rows = [
                { key: "head", label: "头部击中", value: source.head },
                { key: "body", label: "身体击中", value: source.body },
                { key: "legs", label: "腿部击中", value: source.legs },
            ];
            const percentages = rows.map(row => details.toNumber(row.value && row.value.percentage)).filter(value => value !== null);
            const maximum = percentages.length ? Math.max(...percentages) : null;
            return rows.map(row => {
                const percentage = details.toNumber(row.value && row.value.percentage);
                return {
                    ...row,
                    hits: details.formatInteger(row.value && row.value.hits),
                    percentage: details.formatPercent(percentage),
                    accent: maximum !== null && percentage === maximum,
                };
            });
        },
        roles() {
            return details.normalizeRoles(details.sectionData(this.detailData.roles));
        },
        agents() {
            return details.sectionData(this.detailData.agents);
        },
        weapons() {
            return details.sectionData(this.detailData.weapons);
        },
        matches() {
            return Array.isArray(this.trackerInfo.matchInfoList) ? this.trackerInfo.matchInfoList : [];
        },
        visibleAgents() {
            return this.agentsExpanded ? this.agents : this.agents.slice(0, this.initialRows);
        },
        visibleWeapons() {
            return this.weaponsExpanded ? this.weapons : this.weapons.slice(0, this.initialRows);
        },
        visibleMatches() {
            return this.matches.slice(0, 6);
        },
        hasPerformance() {
            return Boolean(this.accuracyData || this.roles.length);
        },
        agentNameMap() {
            return this.matches.reduce((result, match) => {
                const englishName = match.heroName || (match.recordDetails && match.recordDetails.heroName);
                if (englishName && match.heroNameCn) result[englishName] = match.heroNameCn;
                return result;
            }, {});
        },
        leadingWeaponKills() {
            return Math.max(details.toNumber(this.weapons[0] && this.weapons[0].kills) || 0, 1);
        },
    },
    methods: {
        openMatch(match) {
            if (this.matchNavigationEnabled) {
                this.$emit("open-match", match);
            }
        },
        formatDecimal: details.formatDecimal,
        formatInteger: details.formatInteger,
        formatPercent: details.formatPercent,
        localizeAgent: details.localizeAgent,
        localizeRole: details.localizeRole,
        localizeWeapon: details.localizeWeapon,
        roleIcon: details.roleIcon,
        roleWidth(role) {
            if (role.missing) return "0%";
            const value = details.toNumber(role.winRate);
            return `${Math.max(0, Math.min(100, value === null ? 0 : value))}%`;
        },
        roleMatchesText(role) {
            if (role.missing) return "0场";
            const matches = details.toNumber(role.matches);
            return matches === null ? "--" : `${Math.round(matches)}场`;
        },
        roleMetric(role, key, percent = false) {
            if (role.missing) return "--";
            const value = details.toNumber(role[key]);
            if (value === null) return "--";
            return percent ? details.formatPercent(value) : details.formatDecimal(value, 1);
        },
        winLossText(role) {
            if (role.missing) return "--";
            const wins = details.toNumber(role.wins);
            const losses = details.toNumber(role.losses);
            return wins === null || losses === null ? "--" : `${Math.round(wins)}-${Math.round(losses)}`;
        },
        killWidth(weapon) {
            return `${details.weaponProgress(weapon.kills, this.leadingWeaponKills)}%`;
        },
        agentImage(agent) {
            return details.gamerskyAgentImage(agent && agent.name);
        },
        toggleAgents() {
            if (this.agents.length > this.initialRows) this.agentsExpanded = !this.agentsExpanded;
        },
    },
};
</script>

<style lang="scss" scoped>
.valorant-record-sections { margin-top: 20px; color: #fff; }
.agents-section, .weapons-section { margin-top: 16px; }
.matches-section { min-height: 454.5px; margin-top: 20px; }
.section-title { margin: 0 0 12px; color: #fffffd; font-size: 16px; line-height: 22px; font-weight: 600; letter-spacing: 0; }
.section-heading { display: flex; align-items: center; justify-content: space-between; }
.section-heading .section-title { margin-bottom: 12px; }
.matches-section .section-title { color: #fff; }
.all-link { display: flex; align-items: center; gap: 4px; margin: 0 0 12px; padding: 0; border: 0; background: transparent; color: rgba(255,255,255,.6); font-size: 12px; line-height: 17px; }
.all-link img { width: 12px; height: 12px; }
.detail-card, .detail-table { background: rgba(255,255,255,.1); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
.performance-grid { display: flex; flex-direction: column; gap: 8px; }
.accuracy-card { height: 104px; padding: 12px; box-sizing: border-box; }
.accuracy-content { display: flex; align-items: flex-start; gap: 12px; height: 80px; }
.accuracy-figure { width: 32px; height: 80px; margin: 0 12px; flex: 0 0 auto; }
.accuracy-values { display: flex; min-width: 0; padding: 2px; flex: 1; flex-direction: column; gap: 4px; box-sizing: border-box; }
.accuracy-row { display: grid; grid-template-columns: minmax(66px,100px) 60px 60px; align-items: center; justify-content: space-between; height: 22px; color: rgba(255,255,255,.5); font-size: 11px; line-height: 18px; font-weight: 500; }
.accuracy-row > span { line-height: 13.8px; }
.accuracy-row b, .accuracy-row em { color: #efefef; font-style: normal; font-weight: 500; }
.accuracy-row em { font-size: 14px; line-height: 20px; text-align: left; }
.accuracy-row b { font-size: 12px; text-align: right; white-space: nowrap; }
.accuracy-row b small { color: #99abbf; font-size: 12px; font-weight: 400; }
.accuracy-row.accent em { color: #5adbc0; }
.roles-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
.role-card { min-width: 0; height: 77px; padding: 12px; box-sizing: border-box; }
.role-summary { display: flex; align-items: flex-start; gap: 8px; }
.role-summary > img { width: 24px; height: 24px; flex: 0 0 auto; object-fit: contain; }
.role-overview { min-width: 0; flex: 1; }
.role-heading { display: flex; align-items: center; justify-content: space-between; color: rgba(255,255,255,.6); font-size: 12px; line-height: 17px; font-weight: 400; white-space: nowrap; }
.role-progress { height: 3px; margin-top: 2px; overflow: hidden; background: rgba(255,255,255,.1); }
.role-progress i { display: block; height: 100%; background: #5adbc0; }
.role-values { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-left: 32px; }
.role-values span { display: flex; flex-direction: column; align-items: center; min-width: 0; }
.role-values b { color: #efefef; font-size: 10px; line-height: 14px; font-weight: 500; white-space: nowrap; }
.role-values small { color: rgba(255,255,255,.4); font-size: 8px; line-height: 11px; }
.table-head { display: flex; align-items: center; height: 14px; margin-bottom: 8px; padding: 0 12px; color: rgba(255,255,255,.4); font-size: 10px; line-height: 14px; }
.detail-table { padding: 12px; overflow: hidden; box-sizing: border-box; }
.table-row { min-width: 0; font-size: 11px; line-height: 16px; }
.agent-head { gap: 12px; }
.agent-avatar-head { width: 30px; flex: 0 0 30px; }
.agent-head-values, .agent-row-values { display: grid; grid-template-columns: minmax(48px,1.35fr) repeat(4,minmax(0,1fr)); flex: 1; align-items: center; min-width: 0; }
.agent-head-values span:first-child, .agent-primary { min-width: 0; text-align: left; }
.agent-head-values span:not(:first-child), .agent-row-values > span { text-align: center; white-space: nowrap; }
.agent-row { display: flex; align-items: center; gap: 12px; height: 34px; }
.agent-row + .agent-row { margin-top: 16.5px; position: relative; }
.agent-row + .agent-row::before { position: absolute; top: -8.5px; left: 0; width: 100%; height: .5px; background: rgba(255,255,255,.1); content: ""; }
.agent-avatar { width: 30px; height: 30px; flex: 0 0 30px; border-radius: 6px; object-fit: cover; background: rgba(255,255,255,.08); }
.agent-primary { display: flex; flex-direction: column; justify-content: center; min-width: 0; }
.agent-primary strong, .agent-primary small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agent-primary strong { color: #fff; font-size: 12px; line-height: 17px; font-weight: 400; }
.agent-primary small { color: rgba(255,255,255,.6); font-size: 11px; line-height: 15px; }
.agent-row-values > span { color: #fffffd; font-weight: 500; }
.weapon-head { display: grid; grid-template-columns: 60px minmax(74px,1fr) 40px 40px 20px; height: 18px; column-gap: 12px; }
.weapon-head span:nth-child(2), .weapon-head span:last-child { text-align: right; }
.weapon-head span:nth-child(3), .weapon-head span:nth-child(4) { text-align: center; }
.weapon-row { display: grid; grid-template-columns: 60px minmax(74px,1fr) 40px 40px 20px; align-items: center; height: 25.875px; column-gap: 12px; }
.weapon-row + .weapon-row { margin-top: 20.5px; position: relative; }
.weapon-row + .weapon-row::before { position: absolute; top: -10.5px; left: 0; width: 100%; height: .5px; background: rgba(255,255,255,.1); content: ""; }
.weapon-image-cell { display: flex; align-items: center; justify-content: center; width: 60px; height: 25.875px; box-sizing: border-box; }
.weapon-image-cell img { display: block; width: 60px; height: 20px; object-fit: contain; object-position: center; }
.weapon-kill-cell { min-width: 0; }
.weapon-kill-cell > div { display: flex; align-items: center; justify-content: space-between; color: #fff; font-size: 12px; line-height: 17px; white-space: nowrap; }
.weapon-kill-cell strong { overflow: hidden; font-weight: 400; text-overflow: ellipsis; }
.weapon-kill-cell span { color: rgba(255,255,255,.6); }
.weapon-kill-cell i { display: block; width: 100%; height: 3px; margin-top: 2px; overflow: hidden; background: rgba(255,255,255,.1); }
.weapon-kill-cell i b { display: block; height: 100%; background: #5adbc0; }
.weapon-row > span { color: #fffffd; font-size: 12px; line-height: 14px; font-weight: 500; text-align: center; white-space: nowrap; }
.weapon-row > span:last-child { text-align: right; }
.expand-button { display: flex; align-items: center; justify-content: center; gap: 2px; width: 100%; height: 26px; margin-top: 8px; padding: 0; border: 0; background: rgba(255,255,255,.1); color: rgba(255,255,255,.5); font-size: 10px; }
.expand-button img { width: 8px; height: 8px; transform: rotate(-90deg); transition: transform .2s ease; }
.expand-button img.expanded { transform: rotate(90deg); }
.match-list { display: flex; flex-direction: column; gap: 4px; }
.match-list::after { width: 100%; height: .5px; background: rgba(255,255,255,.1); content: ""; }
.record-empty { width: 100%; height: 170px; background: url("../../assets/image/icon_PageWuNeiRong.png") no-repeat center / 200px 170px; }

@media (max-width: 340px) {
    .accuracy-card, .detail-table { padding-left: 9px; padding-right: 9px; }
    .role-card { padding-left: 6px; padding-right: 6px; }
    .role-summary { gap: 5px; }
    .role-summary > img { width: 20px; height: 20px; }
    .role-heading { font-size: 10px; }
    .role-values { padding-left: 25px; }
    .accuracy-content { gap: 7px; }
    .accuracy-figure { margin-left: 7px; margin-right: 7px; }
    .accuracy-row { grid-template-columns: minmax(58px,1fr) 50px 49px; font-size: 10px; }
    .accuracy-row em { font-size: 12px; }
    .accuracy-row b, .accuracy-row b small { font-size: 10px; }
    .agent-head { gap: 8px; }
    .agent-avatar-head, .agent-avatar { width: 28px; flex-basis: 28px; }
    .agent-row { gap: 8px; }
    .agent-head-values, .agent-row-values { grid-template-columns: minmax(43px,1.3fr) repeat(4,minmax(0,1fr)); }
    .weapon-head, .weapon-row { grid-template-columns: 54px minmax(56px,1fr) 36px 36px 20px; column-gap: 7px; }
    .weapon-image-cell, .weapon-image-cell img { width: 54px; }
    .weapon-row > span { font-size: 11px; }
}
</style>
